/**
 * The one database client of this tab. A second `openDb()` in the same tab would queue
 * behind the first and report `dipegang_tab_lain` (section 02), so every screen goes
 * through `getDb()`. Creating it also wires the worker's events into the UI store: sync
 * state, lock state, ready info, plus the browser's online signal and the app's return into
 * view (each asks the worker for a run at once: `sync:segera`), whether a guided flow is on
 * screen (`sync:alur`), the app badge and the storage-persistence request. Release 26: every
 * save passes `tolakTulisPindah` first (a device moved to the new address saves nothing here).
 */
import { type DbClient, openDb } from '../db/open'
import type { SyncState } from '../db/protocol'
import { jumlahLencana, terapkanLencana } from '../sync/badge'
import { penyimpananPeramban } from './alamat'
import { pantauAlur } from './alurTerbuka'
import { tolakTulisPindah } from './pindahAlamat'
import { browserStorage } from './store/tampilan'
import { kerjaTersimpan } from './store/tempatKerja'
import { useUi } from './store/ui'

let client: DbClient | null = null

export function getDb(): DbClient {
  // Plan 10: every scope carries the person's own parish of work (./store/tempatKerja).
  if (client === null) {
    client = bind(
      openDb({
        kerjaUntuk: (scope) => kerjaTersimpan(browserStorage(), scope),
        tolakTulis: () => tolakTulisPindah(penyimpananPeramban()),
      }),
    )
  }
  return client
}

function bind(db: DbClient): DbClient {
  const ui = () => useUi.getState()
  const applySync = (state: SyncState) => ui().setSync(state)

  db.onSyncState(applySync)
  applySync(db.syncState)
  db.onLockState((lock) => ui().setLock(lock))
  ui().setLock(db.lockState)
  db.ready.then(
    (info) => ui().setReady(info),
    () => {},
  )

  if (typeof window !== 'undefined') {
    const setOnline = () => ui().setOnline(navigator.onLine)
    setOnline()
    // Release 17 (owner QoL 9): the link back, or the app back in view, sends what waits at
    // once with the back-off reset. The worker learns about the link from the run itself;
    // without a session (or after a wipe code) nothing runs and nothing is touched.
    const segera = (pemicu: 'daring' | 'kembali') =>
      db.ready.then(() => db.request('sync:segera', { pemicu })).catch(() => {})
    window.addEventListener('online', () => {
      setOnline()
      void segera('daring')
    })
    window.addEventListener('offline', setOnline)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void segera('kembali')
    })
    // Release 17 review M-4: a guided flow on screen holds its saves a while (./alurTerbuka).
    pantauAlur((terbuka) => {
      db.ready.then(() => db.request('sync:alur', { terbuka })).catch(() => {})
    })
    navigator.storage
      ?.persist?.()
      .then((granted) => ui().setPersisted(granted))
      .catch(() => {})
  }

  // Badging API (section 16, "Lampu Meja"): the changes waiting to be sent, on the installed
  // app's icon; absent API, no badge, no error.
  useUi.subscribe((state, previous) => {
    const jumlah = jumlahLencana(state.sync)
    if (jumlah !== jumlahLencana(previous.sync)) void terapkanLencana(navigator, jumlah)
  })

  return db
}
